import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { CurrencyValue, CurrencyType, CurrencyUnknown } from '@/typings/api-models';
import { DEFAULT_CURRENCY } from '@/typings/common';
import Select from '../Select';
import s from './Currency.module.scss';
import Label from '../shared/Label';
import Checkbox from '../CheckBox';
import { Input } from '../Input';

const CURRENCY_OPTIONS = [
  { key: 'USD', label: 'USD' },
  { key: 'CAD', label: 'CAD' },
  { key: 'EUR', label: 'EUR' },
  { key: 'GBP', label: 'GBP' },
];

export type CurrencyProps =
  & Pick<Parameters<typeof NumericFormat>[0],
    | 'id'
    | 'name'
    | 'disabled'
    | 'placeholder'
    | 'suffix'
    | 'min'
    | 'inputMode'
    | 'onBlur'
    | 'onFocus'
  >
  & Pick<Parameters<typeof Label>[0],
    | 'label'
    | 'description'
    | 'hint'
    | 'compact'
  >
  & Pick<Parameters<typeof Select>[0],
    | 'onFocus'
  >
  & {
    value: CurrencyValue | null;
    onChange: (value: CurrencyValue) => void;
    nativeSelect?: boolean;
    disabled?: boolean;
    optional?: boolean;
    error?: string;
    allowUnknownValues?: boolean;
    isEditor?: boolean;
  };

export const DEFAULT_VALUE: CurrencyValue = {
  amount: 0,
  currency: DEFAULT_CURRENCY,
  text: null,
};

const makeValidNumber = (n: number | CurrencyUnknown| undefined | null): number | null | CurrencyUnknown => {
  if (n === null || n === undefined || Number.isNaN(n)) {
    return DEFAULT_VALUE.amount;
  }
  return n;
};

const getCurrencySymbol = (currency: CurrencyType): string => {
  switch (currency) {
  case 'USD':
    return '$';
  case 'EUR':
    return '€';
  case 'GBP':
    return '£';
  case 'CAD':
    return 'C$';
  default:
    return '';
  }
};

const Currency: React.FC<CurrencyProps> = ({
  onChange,
  onFocus,
  label,
  optional,
  description,
  disabled,
  error,
  hint,
  value,
  nativeSelect,
  id,
  name,
  compact,
  placeholder,
  suffix,
  min,
  inputMode,
  onBlur,
  isEditor,
  allowUnknownValues = false,
}) => {
  const [currency, setCurrency] = useState(value?.currency ?? DEFAULT_VALUE.currency);
  const [amount, setAmount] = useState(makeValidNumber(value?.amount));
  const [text, setText] = useState(value?.text || null);
  const [isText, setIsText] = useState(Boolean(value?.text));
  const [isUnknown, setIsUnknown] = useState(value?.isUnknown || false);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleBlur = useCallback(() => {
    onChangeRef.current({
      amount,
      currency,
      isUnknown,
      text,
    });
  }, [amount, currency, isUnknown, text, onBlur]);

  useEffect(() => {
    setCurrency(value?.currency ?? DEFAULT_VALUE.currency);
  }, [value?.currency]);

  useEffect(() => {
    handleBlur();
  }, [currency]);

  const handleChangeAmount = useCallback(
    (values: { floatValue?: number }) => {
      const { floatValue } = values;
      setAmount(makeValidNumber(floatValue));
    },
    [setAmount],
  );

  useEffect(() => {
    setAmount(makeValidNumber(value?.amount ?? undefined));
  }, [value?.amount, setAmount]);

  const handleChangeCurrency = useCallback((newCurrency: string) => {
    setCurrency(newCurrency as CurrencyType);
  }, []);

  const handleIsUnknownChange = useCallback((newIsUnknown: boolean) => {
    const nextValue: CurrencyValue = {
      amount: newIsUnknown ? 0 : amount,
      currency,
      isUnknown: newIsUnknown,
      text: newIsUnknown ? null : text,
    };

    setIsUnknown(newIsUnknown);
    if (newIsUnknown) {
      setIsText(false);
      setText(null);
      setAmount(0);
    }

    onChangeRef.current(nextValue);
  }, [amount, currency, text]);

  const handleIsTextChange = useCallback((newIsText: boolean) => {
    setIsText(newIsText);
    if(newIsText) {
      setIsUnknown(false);
      setAmount(0);
    } else {
      setText(null);
    }
  }, []);

  return (
    <div className={s.wrapper}>
      <Label label={label || ''} required={!optional} description={description} errorMessage={error} hint={hint} compact={compact}>
        <div className={classNames(s['input-component'], error && s.error)}>
          {!isText && (
            <Select
              value={currency}
              label="Currency"
              options={CURRENCY_OPTIONS}
              onChange={handleChangeCurrency}
              onBlur={handleBlur}
              hideLabel
              native={nativeSelect}
              placeholder=""
              disabled={disabled}
              onFocus={onFocus}
              ignoreKeyboardNavigation
              canClearSelection={false}
            />
          )}
          {
            (!isUnknown && !isText)
              ? (
                <NumericFormat
                  id={id}
                  name={name}
                  placeholder={placeholder}
                  suffix={suffix}
                  min={min}
                  inputMode={inputMode}
                  onBlur={handleBlur}
                  disabled={disabled}
                  value={amount?.toString() || ''}
                  onValueChange={handleChangeAmount}
                  onFocus={onFocus}
                  prefix={getCurrencySymbol(currency)}
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator=","
                  decimalSeparator="."
                  allowedDecimalSeparators={['.', ',']}
                />
              )
              : (
                <Input
                  value={isText ? text : 'Unknown'}
                  onChange={(v) => {
                    if (isText) {
                      setText(v);
                    }
                  }}
                  onBlur={handleBlur}
                  disabled={disabled}
                />
              )
          }
        </div>
        <div>
          {!disabled && <div className={s.checkboxes}>
            {(allowUnknownValues && !isEditor) && (
              <Checkbox
                label="Mark as unknown"
                size='sm'
                isChecked={isUnknown}
                onChange={handleIsUnknownChange}
              />
            )}
            {!isEditor && (
              <Checkbox
                label="Override with text"
                size='sm'
                isChecked={isText}
                onChange={handleIsTextChange}
              />
            )}
          </div>
          }
        </div>
      </Label>
    </div>
  );
};

export default Currency;
